import React from 'react';
import { Offline } from 'react-detect-offline';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import svg4everybody from 'svg4everybody';

import { IApplicationState } from './store/applicationState';
import userManager from './userManager';

import Footer from './components/footer/Footer';
import Header from './components/header/Header';

import ErrorBoundary from './ErrorBoundary';
import ScrollToTop from './ScrollToTop';

import NetworkChooserIndex from './pages/networkChooser/NetworkChooserIndex';
import CallbackIndex from './pages/callback/CallbackIndex';
import GroupIndex, { IRouterParams as IGroupRouterParams } from './pages/group/GroupIndex';
import Search from './pages/search/Search';
import LoggedOutIndex from './pages/loggedOut/loggedOutIndex';
import LogInIndex from './pages/logIn/LogInIndex';
import MeetingIndex, { IRouterParams as IMeetingRouterParams } from './pages/meetings/MeetingIndex';
import NotFoundIndex from './pages/notFound/NotFoundIndex';
import ProfileIndex, { IRouterParams as IProfileRouterParams } from './pages/profile/ProfileIndex';
import SilentRenewIndex from './pages/silentRenew/SilentRenewIndex';
import MessagesIndex, { IMessagesRouterParams } from './pages/message/MessagesIndex';
import MessageThreadIndex, { IMessageThreadRouterParams } from './pages/message/MessageThreadIndex';
import StatsIndex, { IRouterParams as IStatsRouterParams } from './pages/stats/StatsIndex';
import MaintenanceIndex, { IRouterParams as IMaintenanceRouterParams } from './pages/maintenance/MaintenanceIndex';
import UpcommingMeetingsIndex from './pages/tools/UpcommingMeetingsIndex'

interface IStateProps {
    user: any;
    viewerIsDi: boolean | null;
}

type Props = IStateProps;

svg4everybody();

let isLoggingOut = false;

const logOut = async () => {
    //console.log('App logout indicated');
    isLoggingOut = true;
    (await userManager).stopSilentRenew();
    //console.log('Silent renew stopped', (await userManager).settings);
};

class App extends React.Component<Props, {}> {
    public render() {
        return <React.Fragment>
            <Router>
                <ErrorBoundary>
                    <Offline polling={false}>
                        <div className="service-banner">
                            <div className="w--lg">
                                <div className="service-banner__inner">
                                    <p>Du er offline og derfor vises kun ældre data.</p>
                                </div>
                            </div>
                        </div>
                    </Offline>
                    <ScrollToTop />
                    <Header logoutCallBack={logOut} />
                    <main>
                        {!isLoggingOut && (!this.props.user || this.props.user.expired) ?
                            <Switch>
                                <Route path="/logged-out" component={LoggedOutIndex} />
                                <Route path="/callback.html" component={CallbackIndex} />
                                <Route path="/silent-renew.html" component={SilentRenewIndex} />
                                <Route component={LogInIndex} />
                            </Switch>
                            :
                            <Switch>
                                <Route exact={true} path="/" component={NetworkChooserIndex} />
                                <Route path="/Group/:groupId/Meeting/:meetingId" component={this.renderMeetingIndex} />
                                <Route path="/Group/:groupId/Messages" component={this.renderMessagesIndex} />
                                <Route path="/Group/:groupId/Message/:threadId" component={this.renderMessageThreadIndex} />
                                <Route path="/Group/:groupId/Stats" component={this.renderStatsIndex.bind(this)} />
                                <Route path="/Group/:groupId/Maintenance" component={this.renderMaintenanceIndex.bind(this)} />
                                <Route path="/Group/:id" component={this.renderGroupIndex} />
                                <Route path="/Search" component={this.renderSearch} />
                                <Route path="/Profile/:personId" component={this.renderProfileIndex} />
                                <Route path="/Tools/UpcommingMeetings" component={this.renderUpcommingMeetingsIndex.bind(this)} />
                                <Route path="/callback.html" component={CallbackIndex} />
                                <Route path="/silent-renew.html" component={SilentRenewIndex} />
                                <Route component={NotFoundIndex} />
                            </Switch>
                        }
                    </main>
                    <Footer />
                </ErrorBoundary>
            </Router>
        </React.Fragment>;
    }

    private renderMeetingIndex(params: RouteComponentProps<IMeetingRouterParams>) {
        return <MeetingIndex key={`${params.match.params.groupId}-${params.match.params.meetingId}`} />;
    }

    private renderMessagesIndex(params: RouteComponentProps<IMessagesRouterParams>) {
        return <MessagesIndex key={params.match.params.groupId} />;
    }

    private renderMessageThreadIndex(params: RouteComponentProps<IMessageThreadRouterParams>) {
        return <MessageThreadIndex key={`${params.match.params.groupId}-${params.match.params.threadId}`} />;
    }

    private renderStatsIndex(params: RouteComponentProps<IStatsRouterParams>) {
        if (this.props.viewerIsDi) {
            return <StatsIndex key={params.match.params.groupId} />;
        } else {
            return <NotFoundIndex />;
        }
    }

    private renderMaintenanceIndex(params: RouteComponentProps<IMaintenanceRouterParams>) {
        if (this.props.viewerIsDi) {
            return <MaintenanceIndex groupId={params.match.params.groupId} key={params.match.params.groupId}/>;
        } else {
            return <NotFoundIndex />;
        }
    }

    private renderGroupIndex(params: RouteComponentProps<IGroupRouterParams>) {
        return <GroupIndex key={params.match.params.id} />;
    }
    private renderSearch(params: RouteComponentProps<IGroupRouterParams>) {
        return <Search />;
    }

    private renderProfileIndex(params: RouteComponentProps<IProfileRouterParams>) {
        return <ProfileIndex key={params.match.params.personId} logoutCallBack={logOut} />;
    }

    private renderUpcommingMeetingsIndex() {
        if (this.props.viewerIsDi) {
            return <UpcommingMeetingsIndex />;
        } else {
            return <NotFoundIndex />;
        }
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => ({
    user: state.oidc.user,
    viewerIsDi: state.oidc.user && state.oidc.user.profile ? state.oidc.user.profile.role === 'DI' : false
});

const container = connect(mapStateToProps)(App);

export default container;